import { Box } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';

/**
 * PageFrame — canonical content frame for every authenticated/admin surface.
 *
 * Three tones:
 *
 *   tone="content" (default) — fills the available column width on wide
 *     viewports (productivity-app pattern: content bands hug the viewport
 *     edges, no fixed 1280-max clamp). Standard horizontal gutters keep
 *     the content off the edge. Right for dashboards, lists, detail
 *     pages, admin tables.
 *
 *   tone="form" — 720px max. Right for settings, onboarding, single-form
 *     pages. Keeps line-length comfortable for label+input pairs without
 *     widow-ing the column on widescreens.
 *
 *   tone="full" — no max width AND no horizontal padding. The route owns
 *     its own gutters (inbox two-pane, browse list+map, anything that
 *     wants edge bleed all the way to viewport).
 *
 * Horizontal gutters use `clamp(16px, 4vw, 32px)` on content/form so they
 * breathe between mobile (16px) and desktop (32px) without breakpoint
 * stair-steps. Vertical padding is `xl` to match the previous AppShell
 * default; `compact` shrinks it for two-pane routes where the frame is
 * mostly just a top spacer.
 */
export function PageFrame({
  children,
  tone = 'content',
  compact = false,
  style,
}: {
  children: ReactNode;
  tone?: 'content' | 'form' | 'full';
  /** Drop vertical padding from `xl` to `lg`. Use when the page owns its own internal rhythm. */
  compact?: boolean;
  style?: CSSProperties;
}) {
  const verticalPadding = compact
    ? 'var(--mantine-spacing-lg)'
    : 'var(--mantine-spacing-xl)';

  if (tone === 'full') {
    return (
      <Box
        style={{
          paddingBlock: verticalPadding,
          ...style,
        }}
      >
        {children}
      </Box>
    );
  }

  if (tone === 'form') {
    return (
      <Box
        style={{
          maxWidth: 720,
          marginInline: 'auto',
          paddingInline: 'clamp(16px, 4vw, 32px)',
          paddingBlock: verticalPadding,
          ...style,
        }}
      >
        {children}
      </Box>
    );
  }

  // content — no max-width, just gutters. Fills the AppShell main column.
  return (
    <Box
      style={{
        paddingInline: 'clamp(16px, 4vw, 32px)',
        paddingBlock: verticalPadding,
        ...style,
      }}
    >
      {children}
    </Box>
  );
}
